import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiPlus, FiTrash2, FiEdit2, FiCheck, FiXCircle } = FiIcons;

const BoardManager = ({ 
  boards, 
  currentBoardId, 
  onClose, 
  onBoardSwitch, 
  onCreateBoard, 
  onDeleteBoard,
  onUpdateBoard 
}) => {
  const [newBoardName, setNewBoardName] = useState('');
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [editingBoardName, setEditingBoardName] = useState('');

  const handleCreateBoard = (e) => {
    e.preventDefault();
    if (newBoardName.trim()) {
      onCreateBoard(newBoardName.trim());
      setNewBoardName('');
    }
  };

  const handleDeleteBoard = (boardId) => {
    if (window.confirm('Are you sure you want to delete this board? All tasks will be lost.')) {
      onDeleteBoard(boardId);
    }
  };

  const startEditBoard = (board) => {
    setEditingBoardId(board.id);
    setEditingBoardName(board.name);
  };

  const cancelEditBoard = () => {
    setEditingBoardId(null);
    setEditingBoardName('');
  };

  const saveEditBoard = (boardId) => {
    if (editingBoardName.trim() && editingBoardName !== boards[boardId].name) {
      onUpdateBoard(boardId, editingBoardName.trim());
    }
    cancelEditBoard();
  };

  const boardList = Object.values(boards);

  return (
    <motion.div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Manage Boards</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Create New Board */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Create New Board</h3>
            <form onSubmit={handleCreateBoard} className="flex space-x-2">
              <input
                type="text"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                placeholder="Enter board name..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <motion.button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Create</span>
              </motion.button>
            </form>
          </div>

          {/* Existing Boards */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Existing Boards</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {boardList.map((board) => (
                <motion.div
                  key={board.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    currentBoardId === board.id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => onBoardSwitch(board.id)}
                      className="text-left flex-1"
                    >
                      {editingBoardId === board.id ? (
                        <input
                          type="text"
                          value={editingBoardName}
                          onChange={(e) => setEditingBoardName(e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                      ) : (
                        <span className="font-medium text-gray-900">{board.name}</span>
                      )}
                    </button>
                    {currentBoardId === board.id && (
                      <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full">Current</span>
                    )}
                  </div>

                  <div className="flex items-center space-x-1">
                    {editingBoardId === board.id ? (
                      <>
                        <motion.button
                          onClick={() => saveEditBoard(board.id)}
                          className="p-1 text-green-600 hover:text-green-700 transition-colors"
                          whileHover={{ scale: 1.1 }}
                        >
                          <SafeIcon icon={FiCheck} className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          onClick={cancelEditBoard}
                          className="p-1 text-gray-600 hover:text-gray-700 transition-colors"
                          whileHover={{ scale: 1.1 }}
                        >
                          <SafeIcon icon={FiXCircle} className="w-4 h-4" />
                        </motion.button>
                      </>
                    ) : (
                      <>
                        <motion.button
                          onClick={() => startEditBoard(board)}
                          className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                          whileHover={{ scale: 1.1 }}
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-3" />
                        </motion.button>
                        {boardList.length > 1 && (
                          <motion.button
                            onClick={() => handleDeleteBoard(board.id)}
                            className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                            whileHover={{ scale: 1.1 }}
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-3" />
                          </motion.button>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BoardManager;